import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ImageRightsAPI } from '../../api/ImageRightsAPI';
import AccountPicturesPanel from './AccountPicturesPanel';
import { thumbnail } from './pictureParts';

import '../dashboard/components/Marker-UI.css';
import '../ProfilePictures.css';
import './ImageRights.css';
import PageTitle from '../../components/PageTitle';
import SinglePictures from './SinglePictures';
import SourceLists from './SourceLists';
import SymbolImages, { openEvents } from './SymbolImages';
import { accountsWithNotes, notesMarkdown } from './notesExport';
import { ownSite, requestText } from './request';

// Where asking an account stands. It shows no picture by itself: each picture is
// cleared on its own, in the panel of the account or under "Einzelbilder".
export const STATUSES = [
  { key: 'OPEN', label: 'Offen' },
  { key: 'REQUESTED', label: 'Angefragt' },
  { key: 'APPROVED', label: 'Erlaubt' },
  { key: 'DENIED', label: 'Abgelehnt' },
];

const FILTERS = [
  { key: 'ALL', label: 'Alle' },
  { key: 'HIDDEN', label: 'Mit versteckten Bildern' },
  ...STATUSES,
];

const formatDate = (value, withTime = false) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return '';
  return date.toLocaleString('de-AT', withTime
    ? { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }
    : { weekday: 'short', day: '2-digit', month: '2-digit' });
};

/** How many of the account's pictures users see: all, some or none. */
const clearedLabel = (account) => {
  if (account.pictures === 0) return 'Keine Bilder';
  if (account.picturesCleared === account.pictures) return 'Alle Bilder freigegeben';
  if (account.picturesCleared === 0) return 'Kein Bild freigegeben';
  return `${account.picturesCleared} von ${account.pictures} freigegeben`;
};

function AccountRow({ account, busy, onStatus, onNote, onOpen }) {
  const [note, setNote] = useState(account.note || '');
  const [copied, setCopied] = useState(false);
  useEffect(() => { setNote(account.note || ''); }, [account.note]);

  const noAccount = !account.handle;
  const copy = async () => {
    // With a paragraph about their site when most of their pictures come from it.
    const text = requestText(account.handle, ownSite(account));
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      window.prompt('Text kopieren:', text);
    }
  };

  return (
    <article className={'ir-row' + (busy ? ' ir-row--busy' : '') + (account.visibleInProd ? ' is-visible' : ' is-hidden')}>
      <button type="button" className="ir-thumbs" onClick={() => onOpen(account)}
        title="Alle Bilder ansehen und einzeln freigeben" aria-label={`Alle Bilder von ${noAccount ? 'Events ohne Account' : `@${account.handle}`} ansehen`}>
        {account.samples.map((sample) => (
          <span key={sample.id} className={'ir-thumb' + (sample.cleared ? ' is-cleared' : '')}>
            <img src={thumbnail(sample.image)} alt="" loading="lazy" />
          </span>
        ))}
      </button>

      <div className="ir-main">
        <div className="ir-head">
          {noAccount ? (
            <span className="ir-handle ir-handle--none">Ohne Instagram-Account</span>
          ) : (
            <a className="ir-handle" href={`https://www.instagram.com/${account.handle}/`} target="_blank" rel="noreferrer">
              @{account.handle}
            </a>
          )}
          <span className={'ir-visibility' + (account.visibleInProd ? ' is-on' : account.picturesCleared > 0 ? ' is-some' : '')}>
            {clearedLabel(account)}
          </span>
          {account.hostActive && <span className="ir-badge" title="Hat einen Host-Code eingelöst und die Nutzungsbedingungen akzeptiert – die Bilder trotzdem einzeln freigeben">Host aktiv</span>}
        </div>

        <p className="ir-facts">
          {account.upcomingEvents > 0
            ? `${account.upcomingEvents} kommende ${account.upcomingEvents === 1 ? 'Event' : 'Events'}`
            : 'Keine kommenden Events'}
          {account.nextEventDate ? ` · nächstes ${formatDate(account.nextEventDate)}` : ''}
          {` · ${account.pictures} ${account.pictures === 1 ? 'Bild' : 'Bilder'}`}
          {account.clubs > 0 ? ` · ${account.clubs} ${account.clubs === 1 ? 'Club' : 'Clubs'}` : ''}
        </p>

        {noAccount ? (
          <p className="ir-hint">Diese Bilder hängen an keinem Instagram-Account, du kannst also niemanden fragen. Gib sie nur frei, wenn nichts Geschütztes drin ist – oder trag beim Event den Account ein.</p>
        ) : (
          <textarea
            className="ir-note"
            rows={2}
            placeholder="Notiz, z. B. „DM am 14.10., Zusage per Mail“"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            onBlur={() => { if ((account.note || '') !== note) onNote(account, note); }}
            disabled={busy}
          />
        )}

        {(account.requestedAt || account.decidedAt) && (
          <p className="ir-dates">
            {account.requestedAt ? `Angefragt ${formatDate(account.requestedAt, true)}` : ''}
            {account.requestedAt && account.decidedAt ? ' · ' : ''}
            {account.decidedAt ? `Entschieden ${formatDate(account.decidedAt, true)}` : ''}
          </p>
        )}
      </div>

      <div className="ir-side">
        <div className="ir-status" role="group" aria-label={`Stand für @${account.handle}`}>
          {STATUSES.map((status) => (
            <button
              key={status.key}
              type="button"
              className={'ir-status-btn ir-status-btn--' + status.key.toLowerCase() + (account.status === status.key ? ' is-active' : '')}
              aria-pressed={account.status === status.key}
              onClick={() => onStatus(account, status.key)}
              disabled={busy || noAccount || account.status === status.key}
            >
              {status.label}
            </button>
          ))}
        </div>
        <div className="ir-actions">
          <button type="button" className="pp-btn ir-open" onClick={() => onOpen(account)}>
            {`Alle ${account.pictures} ${account.pictures === 1 ? 'Bild' : 'Bilder'} ansehen`}
          </button>
        </div>
        {!noAccount && (
          <div className="ir-actions">
            <button type="button" className="pp-btn" onClick={copy}>
              {copied ? 'Kopiert ✓' : 'Anfrage-Text kopieren'}
            </button>
            <a className="pp-btn" href={`https://ig.me/m/${account.handle}`} target="_blank" rel="noreferrer">
              DM öffnen
            </a>
          </div>
        )}
      </div>
    </article>
  );
}

export default function ImageRights() {
  const [view, setView] = useState('ACCOUNTS');
  // The account whose pictures the side panel shows, by handle ('' for none).
  const [openHandle, setOpenHandle] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('ALL');
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await ImageRightsAPI.overview());
    } catch (err) {
      console.error('Fehler beim Laden der Bildrechte:', err);
      setError('Konnte die Bildrechte nicht laden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // What waits on the whitelist (to accept) and the blacklist (to remove), for the tabs.
  const [sources, setSources] = useState(null);
  const loadSources = useCallback(async () => {
    try {
      setSources(await ImageRightsAPI.sources());
    } catch (err) {
      console.error('Fehler beim Laden der Listen:', err);
    }
  }, []);
  useEffect(() => { loadSources(); }, [loadSources]);
  const waitingOn = (list) => (sources ?? []).filter((rule) => rule.list === list)
    .reduce((sum, rule) => sum + (rule.waiting || 0), 0);

  // How many events of a kind with AI symbol pictures still show the Schick artwork, for the tab.
  const [symbolsOpen, setSymbolsOpen] = useState(0);
  const countSymbols = useCallback((symbols) => setSymbolsOpen(openEvents(symbols).length), []);
  useEffect(() => {
    ImageRightsAPI.symbolImages()
      .then(countSymbols)
      .catch((err) => console.error('Fehler beim Laden der KI-Symbolbilder:', err));
  }, [countSymbols]);

  const save = async (account, changes) => {
    setBusy((prev) => new Set(prev).add(account.handle));
    setError(null);
    try {
      const updated = await ImageRightsAPI.update(account.handle, {
        status: changes.status ?? account.status,
        note: changes.note ?? account.note ?? '',
      });
      // The totals follow from every row, so the overview is read again.
      setData((prev) => prev && {
        ...prev,
        accounts: prev.accounts.map((each) => (each.handle === updated.handle ? updated : each)),
      });
      load();
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      setError(`Konnte @${account.handle} nicht speichern.`);
    } finally {
      setBusy((prev) => {
        const next = new Set(prev);
        next.delete(account.handle);
        return next;
      });
    }
  };

  const accounts = useMemo(() => {
    const list = data?.accounts ?? [];
    const term = query.trim().toLowerCase().replace(/^@/, '');
    return list.filter((account) => {
      if (term && !(account.handle || '').includes(term)
        && !account.samples.some((sample) => (sample.title || '').toLowerCase().includes(term))) return false;
      if (filter === 'ALL') return true;
      if (filter === 'HIDDEN') return account.picturesCleared < account.pictures;
      return account.status === filter;
    });
  }, [data, filter, query]);

  const openAccount = openHandle == null ? null
    : (data?.accounts ?? []).find((account) => (account.handle || '') === openHandle) ?? null;
  const closePanel = useCallback(() => setOpenHandle(null), []);

  // Every note with its organiser, as Markdown for an LLM.
  const [notesCopied, setNotesCopied] = useState(false);
  const notesCount = accountsWithNotes(data?.accounts).length;
  const copyNotes = async () => {
    const text = notesMarkdown(data?.accounts);
    try {
      await navigator.clipboard.writeText(text);
      setNotesCopied(true);
      setTimeout(() => setNotesCopied(false), 2500);
    } catch {
      window.prompt('Notizen kopieren:', text);
    }
  };

  const totals = data?.totals;
  const upcomingAll = totals ? totals.upcomingVisible + totals.upcomingHidden : 0;
  const share = upcomingAll ? Math.round((totals.upcomingVisible / upcomingAll) * 100) : 0;
  const countFor = (key) => {
    if (!totals) return 0;
    if (key === 'ALL') return totals.accounts;
    if (key === 'HIDDEN') return (data?.accounts ?? []).filter((account) => account.picturesCleared < account.pictures).length;
    return { OPEN: totals.open, REQUESTED: totals.requested, APPROVED: totals.approved, DENIED: totals.denied }[key];
  };

  return (
    <div className="page">
      <div className="page-header">
        <PageTitle title="Bildrechte" description="Welche Veranstalter erlaubt haben, dass Schick ihre Bilder zeigt. Ohne Erlaubnis sehen Nutzer die Schick-Grafik." />
        <div className="ir-head-actions">
          <button type="button" className="ir-export" onClick={copyNotes} disabled={notesCount === 0}
            title="Alle Notizen mit ihrem Veranstalter als Text kopieren – zum Einfügen in ChatGPT oder Claude">
            {notesCopied ? `Kopiert ✓ (${notesCount})` : `Notizen kopieren${notesCount ? ` (${notesCount})` : ''}`}
          </button>
          <div className="ir-views" role="tablist" aria-label="Ansicht">
            <button type="button" role="tab" aria-selected={view === 'ACCOUNTS'}
              className={view === 'ACCOUNTS' ? 'is-active' : ''} onClick={() => setView('ACCOUNTS')}>
              Nach Veranstalter
            </button>
            <button type="button" role="tab" aria-selected={view === 'PICTURES'}
              className={view === 'PICTURES' ? 'is-active' : ''} onClick={() => setView('PICTURES')}>
              Einzelbilder
            </button>
            <button type="button" role="tab" aria-selected={view === 'WHITELIST'}
              className={view === 'WHITELIST' ? 'is-active' : ''} onClick={() => setView('WHITELIST')}>
              Whitelist
              {waitingOn('WHITE') > 0 && <span className="isl-tab-count isl-tab-count--white">{waitingOn('WHITE')}</span>}
            </button>
            <button type="button" role="tab" aria-selected={view === 'BLACKLIST'}
              className={view === 'BLACKLIST' ? 'is-active' : ''} onClick={() => setView('BLACKLIST')}>
              Blacklist
              {waitingOn('BLACK') > 0 && <span className="isl-tab-count isl-tab-count--black">{waitingOn('BLACK')}</span>}
            </button>
            <button type="button" role="tab" aria-selected={view === 'SYMBOLS'}
              className={view === 'SYMBOLS' ? 'is-active' : ''} onClick={() => setView('SYMBOLS')}>
              KI-Symbolbilder
              {symbolsOpen > 0 && <span className="isl-tab-count isl-tab-count--symbol">{symbolsOpen}</span>}
            </button>
          </div>
        </div>
      </div>

      {view === 'WHITELIST' && <SourceLists key="WHITE" list="WHITE" onChanged={() => { loadSources(); load(); }} />}
      {view === 'BLACKLIST' && <SourceLists key="BLACK" list="BLACK" onChanged={() => { loadSources(); load(); }} />}
      {view === 'SYMBOLS' && <SymbolImages onLoaded={countSymbols} onAssigned={load} />}

      {view === 'WHITELIST' || view === 'BLACKLIST' || view === 'SYMBOLS' ? null : view === 'PICTURES' ? <SinglePictures /> : (<>

      {data && (
        <div className={'ir-env' + (data.permissionRequired ? ' is-prod' : '')}>
          {data.permissionRequired
            ? 'Prod: Nutzer sehen ein fremdes Bild nur, wenn du es einzeln freigegeben hast. Alle anderen bekommen die Schick-Grafik – die Bilder bleiben gespeichert.'
            : 'Diese Umgebung zeigt alle Bilder (Entwicklung, für Screenshots). In Prod gilt die Freigabe pro Bild.'}
        </div>
      )}

      {totals && (
        <section className="ir-summary" aria-label="Überblick">
          <div className="ir-progress">
            <div className="ir-progress-head">
              <strong>{totals.upcomingVisible}</strong> von {upcomingAll} kommenden Events zeigen in Prod ihr echtes Bild
              <span>{share}%</span>
            </div>
            <div className="ir-progress-bar"><span style={{ width: `${share}%` }} /></div>
            <div className="ir-progress-sub">
              {totals.picturesVisible} Bilder freigegeben · {totals.picturesHidden} versteckt · {totals.accounts} Accounts
            </div>
          </div>
        </section>
      )}

      <div className="ir-toolbar">
        <div className="ir-filters" role="tablist" aria-label="Filter">
          {FILTERS.map((option) => (
            <button
              key={option.key}
              type="button"
              role="tab"
              aria-selected={filter === option.key}
              className={'ir-chip' + (filter === option.key ? ' is-active' : '')}
              onClick={() => setFilter(option.key)}
            >
              {option.label}
              <span className="ir-chip-count">{countFor(option.key)}</span>
            </button>
          ))}
        </div>
        <div className="ir-toolbar-right">
          <input
            className="ir-search"
            type="search"
            placeholder="Account oder Event suchen"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <button className="btn-add btn-add--green" type="button" onClick={load} disabled={loading}>
            Neu laden
          </button>
        </div>
      </div>

      {error && <p className="pp-error">{error}</p>}

      {loading && !data ? (
        <div className="pp-sentinel"><div className="pp-spinner" aria-label="Lädt" /></div>
      ) : accounts.length === 0 ? (
        <div className="pp-empty">Keine Accounts in diesem Filter.</div>
      ) : (
        <div className="ir-list">
          {accounts.map((account) => (
            <AccountRow
              key={account.handle || '(none)'}
              account={account}
              busy={busy.has(account.handle)}
              onStatus={(each, status) => save(each, { status })}
              onNote={(each, note) => save(each, { note })}
              onOpen={(each) => setOpenHandle(each.handle || '')}
            />
          ))}
        </div>
      )}
      </>)}

      {openAccount && (
        <AccountPicturesPanel account={openAccount} onClose={closePanel} onChanged={load} />
      )}
    </div>
  );
}
